<template>
	<div ref="rootEl" class="base-datepicker">
		<div class="base-datepicker__header">
			<button
				class="base-datepicker__previous-month"
				aria-label="previous month"
				type="button"
				@click="previousMonth"
			>
				<svg width="18" height="18" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M7 1L1 7l6 6" stroke="#201E1E" stroke-linecap="round" stroke-linejoin="round"/></svg>
			</button>

			<h2 class="base-datepicker__header-content" aria-live="polite">
				{{ header }}
			</h2>

			<button
				class="base-datepicker__next-month"
				aria-label="next month"
				type="button"
				@click="nextMonth"
			>
				<svg width="18" height="18" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1 13l6-6-6-6" stroke="#201E1E" stroke-linecap="round" stroke-linejoin="round"/></svg>
			</button>
		</div>

		<table class="base-datepicker__picker" role="grid" aria-labelledby="id-dialog-label">
			<thead v-if="showDaysHeader" class="base-datepicker__days-header">
				<tr>
					<th v-for="{ abbr, short } in headerDays" :key="short" scope="col" v-bind="{ abbr }">
						{{ short }}
					</th>
				</tr>
			</thead>

			<tbody class="base-datepicker__body">
				<tr v-for="(row, rowIndex) in daysGrid" :key="rowIndex" class="base-datepicker__row">
					<td
						v-for="(day, dayIndex) in row"
						:key="dayIndex"
						class="base-datepicker__day"
						:class="{
							'base-datepicker__day--empty': isEmpty(day),
							'base-datepicker__day--selected': isSelected(day),
							'base-datepicker__day--is-disabled': isDisabled(day),
						}"
					>
						<button
							v-if="!isEmpty(day)"
							class="base-datepicker__day-button"
							:disabled="isDisabled(day)"
							tabindex="-1"
							type="button"
							@click="onDayClicked(day)"
						>
							{{ day }}
						</button>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script lang="ts">
import { zipWith } from 'lodash';
import moment, { Moment, months as getMonths, weekdays, weekdaysShort } from 'moment';
import { defineComponent, PropType, reactive, ref, toRefs, watch } from 'vue';

const EMPTY_DAY = -1;
const months = getMonths();
const fillEmptyCells = (row: number[]) => {
	for (let i = 0; i < row.length; i++) {
		if (!row[i]) {
			row[i] = EMPTY_DAY;
		}
	}
};

// TODO: accessibility

export default defineComponent({
	props: {
		modelValue: {
			type: Object as PropType<Moment | null>,
			default: null,
			validator: moment.isMoment,
		},

		showDaysHeader: {
			type: Boolean,
		},

		disableDay: {
			type: Function as any as PropType<null | ((day: Moment) => boolean)>,
			default: null,
		},
	},

	emits: ['update:modelValue'],

	setup(props, { emit }) {
		const rootEl = ref<HTMLElement>();
		const data = reactive({
			selectedDate: props.modelValue,
			currentDate: props.modelValue?.toISOString() ?? moment().toISOString(),
		});

		watch(
			() => props.modelValue,
			date => {
				if (date !== data.selectedDate) {
					data.selectedDate = date;
				}
			},
		);
		watch(
			() => data.selectedDate,
			() => {
				emit('update:modelValue', data.selectedDate);
				rootEl.value?.dispatchEvent(new Event('input', { bubbles: true, cancelable: false }));
			},
		);

		return {
			...toRefs(data),

			rootEl,
		};
	},

	computed: {
		header(): string {
			const { currentDateMoment } = this;

			return `${months[currentDateMoment.month()]} ${currentDateMoment.year()}`;
		},

		headerDays(): { abbr: string; short: string }[] {
			return zipWith(weekdays(), weekdaysShort(), (abbr, short) => ({ abbr, short }));
		},

		daysGrid(): number[][] {
			const rows: number[][] = [[]];
			const daysInMonth = this.currentDateMoment.daysInMonth();
			const tmpMoment = moment(this.currentDateMoment).date(1);
			const weekDay = tmpMoment.day() - 1 === -1 ? 6 : tmpMoment.day() - 1;

			rows[0][weekDay] = 1;
			fillEmptyCells(rows[0]);

			for (let date = 2, currentRow = 0; date <= daysInMonth; date++) {
				if (rows[currentRow].length === 7) {
					rows.push([]);
					currentRow++;
				}
				rows[currentRow].push(date);
			}

			rows[rows.length - 1].length = 7;
			fillEmptyCells(rows[rows.length - 1]);

			return rows;
		},

		isSelected(): (day: number) => boolean {
			const { selectedDate } = this;

			return day => {
				const date = moment(this.currentDate).date(day);

				return !!selectedDate?.isSame(date, 'day');
			};
		},

		currentDateMoment(): Moment {
			return moment(this.currentDate);
		},
	},

	methods: {
		previousMonth() {
			this.currentDate = moment(this.currentDate).subtract(1, 'month').toISOString();
		},

		nextMonth() {
			this.currentDate = moment(this.currentDate).add(1, 'month').toISOString();
		},

		isEmpty(day: number) {
			return day === EMPTY_DAY;
		},

		isDisabled(day: number) {
			if (this.isEmpty(day) || !this.disableDay) {
				return;
			}

			return this.disableDay(moment(this.currentDateMoment).date(day));
		},

		onDayClicked(day: number) {
			if (this.isSelected(day)) {
				this.selectedDate = null;
			} else {
				this.selectDay(day);
			}
		},

		selectDay(day: number) {
			this.selectedDate = moment(this.currentDate).date(day);
		},
	},
});
</script>

<style lang="scss">
.base-datepicker {
	$self: &;

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 1.4rem;
		color: $color-dark;
	}

	&__day {
		padding: 8px 10px;
		color: $color-gray;

		&--selected {
			#{$self}__day-button {
				color: $color-light;
				border-radius: 8px;
				background-color: $color-dark;
			}
		}

		&--is-disabled {
			#{$self}__day-button {
				pointer-events: none;
			}
		}
	}

	&__day-button {
		width: 32px;
		height: 32px;
		font-size: 1.8rem;
		text-align: center;
	}

	&__previous-month,
	&__next-month {
		width: 40px;
		height: 40px;

		> svg {
			width: 10px;
			height: 20px;
		}
	}
}
</style>
